import type {ReactElement} from 'react';
import React, {useEffect, useState} from 'react';
import {Text, TextSize} from '@Components/text';
import {IconBox, IconBoxSize} from '@Components/icon-box';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Pill, PILL_BACKGROUND_COLOR, PILL_CONTENT_TYPE, PILL_SIZE} from '@Components/pill';
import {noop} from '@Utils/general.util';
import {hasUserSeenAutoBranding, markUserSeenAutoBranding} from '@Libraries/auto-branding-email-maker';
import type {ColorPaletteInfo} from './auto-branding.types';
import {ColorPalette, ColorPaletteSize} from './components/color-palette';
import type {AutoBrandingIntroVisualProps} from './components/auto-branding-intro-visual';
import {AutoBrandingIntroVisual} from './components/auto-branding-intro-visual';
import styles from './auto-branding.module.scss';

export interface AutoBrandingProps {
  firstTimeVisualProps: AutoBrandingIntroVisualProps;
  appliedPalette?: ColorPaletteInfo | null;
  onPaletteAdd?: VoidFunction;
  onPaletteSelect?: VoidFunction;
  onPaletteEdit?: (currentColorPalette: ColorPaletteInfo | null) => void;
}

export function AutoBranding({firstTimeVisualProps, appliedPalette = null, onPaletteAdd = noop, onPaletteSelect = noop, onPaletteEdit = noop}: AutoBrandingProps): ReactElement {
  const [userHasSeen, setUserHasSeen] = useState<boolean>(true);

  const getColorPaletteLabel = (): ReactElement => {
    return (
      <div className={styles.labelContainer}>
        <Text val={window.i18next.t('pmwjs_styles')} size={TextSize.XSMALL} className="content-body" />
        <Pill
          content={{
            type: PILL_CONTENT_TYPE.TEXT,
            text: window.i18next.t('pmwjs_new'),
            isBold: true,
            textColor: 'content-body-white',
          }}
          background={PILL_BACKGROUND_COLOR.SECONDARY_100}
          size={PILL_SIZE.XSMALL}
          className="spacing-p-l-2 spacing-p-r-2"
        />
      </div>
    );
  };

  const doesColorPaletteExist = (): boolean => {
    return appliedPalette !== null && appliedPalette.colors.length > 0;
  };

  const getColorPaletteIconClickHandler = (): VoidFunction => {
    return doesColorPaletteExist()
      ? (): void => {
          onPaletteEdit(appliedPalette);
        }
      : onPaletteAdd;
  };

  const getColorPaletteIcon = (): ReactElement => {
    const icon = doesColorPaletteExist() ? 'icon-pencil' : 'icon-plus';
    const clickHandler = getColorPaletteIconClickHandler();

    return <IconBox icon={icon} className={styles.icon} size={IconBoxSize.SMALL} iconSize={IconSize.SIZE_ICON_16} onClick={clickHandler} />;
  };

  const getAutoBrandingIntroVisualComponent = (): ReactElement => {
    return (
      <AutoBrandingIntroVisual
        {...firstTimeVisualProps}
        closeButtonCta={() => {
          markUserHasSeen(firstTimeVisualProps.closeButtonCta);
        }}
        buttonCta={() => {
          markUserHasSeen(firstTimeVisualProps.buttonCta);
        }}
      />
    );
  };

  const getAutoBrandingComponent = (): ReactElement => {
    return (
      <div className={styles.container}>
        {getColorPaletteLabel()}
        <div className={styles.colorPaletteContainer}>
          <ColorPalette appliedPalette={appliedPalette} size={ColorPaletteSize.SMALL} onClick={onPaletteSelect} />
          {getColorPaletteIcon()}
        </div>
      </div>
    );
  };

  const markUserHasSeen = (callback: VoidFunction | undefined): void => {
    setUserHasSeen(true);
    void markUserSeenAutoBranding();

    if (callback) {
      callback();
    }
  };

  useEffect(() => {
    void hasUserSeenAutoBranding().then((hasSeen) => {
      setUserHasSeen(hasSeen);
    });
  }, []);

  return userHasSeen ? getAutoBrandingComponent() : getAutoBrandingIntroVisualComponent();
}
